import { createModalFocusScope } from '../../components/modal-focus-scope.js';
import { closeListBeforeBackdrop, comboboxKey, initGameFormCombobox } from '../game-form-combobox.js';
import { readErrorMessage } from '../../components/write-request.js';
import { submitDictionaryMutation } from './mutation-request.js';

const UNKNOWN_MESSAGE = 'Could not confirm the change was saved. Reload the page before trying again.';
import { syncReturnQueries } from './query-state.js';

export function initDictionaryDeleteFlow(
    root,
    windowRef,
    fetchImpl = globalThis.fetch,
    commitRefresh = (currentSection, replacementSection) => {
        currentSection.replaceWith(replacementSection);

        return replacementSection;
    },
    dictionaryFeedback = null,
    parseSection = (html, type) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');

        return parsed.querySelector(`[data-dictionary-section][data-dictionary-type="${type}"]`);
    },
) {
    const modal = root.querySelector('[data-dictionary-delete-modal]');
    const dialog = modal?.querySelector('[data-dictionary-delete-dialog]');
    const title = modal?.querySelector('[data-dictionary-delete-title]');
    const summary = modal?.querySelector('[data-dictionary-delete-summary]');
    const impact = modal?.querySelector('[data-dictionary-delete-impact]');
    const feedback = modal?.querySelector('[data-dictionary-delete-feedback]');
    const linkedGamesContainer = modal?.querySelector('[data-dictionary-delete-linked-games]');
    const linkedGamesList = modal?.querySelector('[data-dictionary-delete-linked-games-list]');
    const form = modal?.querySelector('[data-dictionary-delete-form]');
    const confirmButton = modal?.querySelector('[data-dictionary-delete-confirm]');
    const moveTarget = initMoveTarget(modal, () => syncImpact());
    const backdrop = modal?.querySelector('[data-dictionary-delete-backdrop]');
    if (
        !(modal instanceof HTMLElement)
        || !(dialog instanceof HTMLElement)
        || !(title instanceof HTMLElement)
        || !(summary instanceof HTMLElement)
        || !(impact instanceof HTMLElement)
        || !(feedback instanceof HTMLElement)
        || !(linkedGamesContainer instanceof HTMLElement)
        || !(linkedGamesList instanceof HTMLElement)
        || !(form instanceof HTMLFormElement)
        || !(confirmButton instanceof HTMLButtonElement)
        || moveTarget === null
        || typeof fetchImpl !== 'function'
    ) {
        return null;
    }

    let activeSection = null;
    let activeType = '';
    let activeName = '';
    let activeLinkedGameCount = 0;
    let deletePending = false;
    const closeButtons = modal.querySelectorAll('[data-dictionary-delete-close]');
    // The one owner of the dialog's buttons: `Delete` needs an open delete and no request out. A focused
    // button that gets disabled parks focus on the dialog, so it never drops out of it.
    const setPending = (pending) => {
        deletePending = pending;
        if (pending) {
            modal.setAttribute('aria-busy', 'true');
        } else {
            modal.removeAttribute('aria-busy');
        }
        const active = root.activeElement;
        closeButtons.forEach((button) => {
            if (button instanceof HTMLButtonElement) {
                if (pending && button === active) {
                    dialog.focus();
                }
                button.disabled = pending;
            }
        });
        if (pending && confirmButton === active) {
            dialog.focus();
        }
        confirmButton.disabled = pending || activeSection === null;
        confirmButton.textContent = pending ? 'Deleting...' : idleConfirmLabel();
    };
    const idleConfirmLabel = () => {
        if (moveTarget.selectedName() === null) {
            return 'Delete';
        }

        return activeType === 'tag' ? 'Delete and replace' : 'Delete and move';
    };
    const syncImpact = () => {
        if (activeLinkedGameCount === 0) {
            return;
        }

        const dictionaryLabel = dictionaryLabels[activeType] ?? 'entry';
        const games = activeLinkedGameCount === 1 ? 'that game' : 'those games';
        const targetName = moveTarget.selectedName();
        if (targetName === null) {
            impact.textContent = `Deleting it will remove the ${dictionaryLabel} assignment from ${games}.`;
        } else if (activeType === 'tag') {
            impact.textContent = `Deleting it will replace it with "${targetName}" on ${games}. A game that already has "${targetName}" keeps it once.`;
        } else {
            impact.textContent = `Deleting it will move ${games} to "${targetName}".`;
        }
        if (!deletePending) {
            confirmButton.textContent = idleConfirmLabel();
        }
    };
    const close = () => {
        if (modal.hidden || deletePending) {
            return;
        }

        modal.hidden = true;
        form.removeAttribute('action');
        linkedGamesList.replaceChildren();
        linkedGamesContainer.hidden = true;
        feedback.textContent = '';
        feedback.hidden = true;
        confirmButton.disabled = true;
        confirmButton.textContent = 'Delete';
        activeSection = null;
        activeType = '';
        activeName = '';
        activeLinkedGameCount = 0;
        moveTarget.reset([], '');
        focusScope.deactivate();
    };
    const focusScope = createModalFocusScope({
        dialog,
        requestClose: close,
        documentRef: root,
    });
    if (focusScope === null) {
        return null;
    }

    const dictionaryLabels = { hardware: 'hardware', platform: 'platform', tag: 'tag' };
    const backdropOnlyClosedList = backdrop instanceof HTMLElement
        ? closeListBeforeBackdrop(backdrop, moveTarget.combobox)
        : () => false;
    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        if (target.closest('[data-dictionary-delete-close]') !== null) {
            if (target === backdrop && backdropOnlyClosedList()) {
                return;
            }
            close();

            return;
        }

        const deleteButton = target.closest('[data-dictionary-delete]');
        if (!(deleteButton instanceof HTMLButtonElement)) {
            return;
        }

        const item = deleteButton.closest('.hardware-platforms-item');
        if (!(item instanceof HTMLElement)) {
            return;
        }

        const dictionaryType = String(item.dataset.dictionaryType ?? '');
        const dictionaryName = String(item.dataset.dictionaryName ?? '');
        const deleteUrl = String(item.dataset.dictionaryDeleteUrl ?? '');
        const section = item.closest('[data-dictionary-section]');
        if (
            dictionaryName === ''
            || deleteUrl === ''
            || !(section instanceof HTMLElement)
        ) {
            return;
        }

        const linkedGames = Array.from(item.querySelectorAll('[data-dictionary-linked-games-source] li'));
        const linkedGameCount = linkedGames.length;
        const dictionaryLabel = dictionaryLabels[dictionaryType] ?? 'entry';
        activeSection = section;
        activeType = dictionaryType;
        activeName = dictionaryName;
        activeLinkedGameCount = linkedGameCount;
        moveTarget.setWording(dictionaryType === 'tag'
            ? { label: 'Replace tag with', none: 'Remove without replacement' }
            : { label: 'Move games to', none: "Don't move" });
        moveTarget.reset(
            linkedGameCount === 0 ? [] : readMoveTargets(section),
            String(item.dataset.dictionaryId ?? ''),
        );
        form.action = deleteUrl;
        syncReturnQueries(form, windowRef);
        feedback.textContent = '';
        feedback.hidden = true;
        confirmButton.disabled = false;
        title.textContent = `Delete "${dictionaryName}"?`;
        if (linkedGameCount === 0) {
            summary.textContent = `This ${dictionaryLabel} is not used by any games.`;
            impact.textContent = '';
            impact.hidden = true;
            linkedGamesList.replaceChildren();
            linkedGamesContainer.hidden = true;
        } else {
            summary.textContent = `This ${dictionaryLabel} is currently used by ${linkedGameCount} ${linkedGameCount === 1 ? 'game' : 'games'}.`;
            impact.hidden = false;
            syncImpact();
            linkedGamesList.replaceChildren(...linkedGames.map((linkedGame) => linkedGame.cloneNode(true)));
            linkedGamesContainer.hidden = false;
        }

        modal.hidden = false;
        focusScope.activate(deleteButton);
        // Cancel, not the first focusable: the move-target combobox opens its options on focus.
        windowRef.requestAnimationFrame(() => focusScope.focus(dialog.querySelector('[data-dictionary-delete-close]')));
    });

    form.addEventListener('submit', async (event) => {
        if (
            !(activeSection instanceof HTMLElement)
            || !Object.hasOwn(dictionaryLabels, activeType)
            || typeof form.action !== 'string'
            || form.action === ''
        ) {
            return;
        }

        event.preventDefault();
        if (deletePending) {
            return;
        }

        const requestSection = activeSection;
        const requestType = activeType;
        const requestName = activeName;
        const requestTargetName = moveTarget.selectedName();
        feedback.textContent = '';
        feedback.hidden = true;
        setPending(true);
        let failureMessage = UNKNOWN_MESSAGE;

        try {
            const response = await submitDictionaryMutation(
                form,
                requestType,
                windowRef,
                fetchImpl,
            );
            if (!response.ok) {
                failureMessage = await readErrorMessage(response, 'Deletion failed. Try again.');
                throw new Error(`Dictionary delete failed with status ${response.status}`);
            }

            const replacement = parseSection(await response.text(), requestType);
            if (
                !(replacement instanceof HTMLElement)
                || replacement.dataset.dictionaryType !== requestType
            ) {
                throw new Error('Invalid dictionary delete response');
            }

            const committedSection = await commitRefresh(requestSection, replacement);
            if (!(committedSection instanceof HTMLElement)) {
                throw new Error('Dictionary delete refresh failed');
            }
            setPending(false);
            close();
            dictionaryFeedback?.show?.(
                committedSection,
                `${dictionaryLabels[requestType][0].toUpperCase()}${dictionaryLabels[requestType].slice(1)} "${requestName}" has been deleted.${requestTargetName === null ? '' : ` Its games were moved to "${requestTargetName}".`}`,
            );
            const heading = committedSection.querySelector('h2');
            if (heading instanceof HTMLElement) {
                heading.setAttribute('tabindex', '-1');
                heading.focus();
            }
        } catch {
            setPending(false);
            feedback.textContent = failureMessage;
            feedback.hidden = false;
            // Focus returns to `Delete` unless it moved on inside the dialog.
            const active = root.activeElement;
            if (active === dialog || !dialog.contains(active)) {
                confirmButton.focus();
            }
        }
    });

    return { close };
}

/**
 * The section's whole dictionary as `[{id, name}]`, rendered with its results so it follows every
 * refresh (the visible rows are only one page of it).
 */
function readMoveTargets(section) {
    try {
        const targets = JSON.parse(section.querySelector('[data-dictionary-entries]')?.dataset.dictionaryTargets ?? '[]');

        return Array.isArray(targets) ? targets : [];
    } catch {
        return [];
    }
}

/**
 * "Move games to": the native select is the submitted value (`form=` ties it to the delete form);
 * the shared combobox only draws and searches over it, like the game form's status field. Options
 * are read from the current select on every open, so one init serves every delete.
 */
function initMoveTarget(modal, onChange) {
    const field = modal?.querySelector('[data-dictionary-delete-target]');
    const label = modal?.querySelector('[data-dictionary-delete-target-label]');
    const select = modal?.querySelector('[data-dictionary-delete-target-select]');
    const comboboxElement = modal?.querySelector('[data-dictionary-delete-target-combobox]');
    const input = modal?.querySelector('[data-dictionary-delete-target-input]');
    const toggle = modal?.querySelector('[data-dictionary-delete-target-toggle]');
    const list = modal?.querySelector('[data-dictionary-delete-target-options]');
    if (
        !(field instanceof HTMLElement)
        || !(select instanceof HTMLSelectElement)
        || !(comboboxElement instanceof HTMLElement)
        || !(input instanceof HTMLInputElement)
        || !(toggle instanceof HTMLButtonElement)
        || !(list instanceof HTMLUListElement)
        || !(label instanceof HTMLElement)
    ) {
        return null;
    }

    const options = () => Array.from(select.options).map((option) => ({
        type: 'option',
        value: option.value,
        label: option.textContent.trim(),
    }));
    const selectedName = () => (select.value === ''
        ? null
        : options().find((option) => option.value === select.value)?.label ?? null);
    // The empty choice shows as the placeholder, so the field reads "Don't move" until a pick.
    const showSelected = () => {
        input.value = selectedName() ?? '';
    };

    const combobox = initGameFormCombobox({
        root: field,
        input,
        toggle,
        list,
        buildOptions: (query) => {
            const queryKey = comboboxKey(query);
            const matches = options().filter((option) => comboboxKey(option.label).includes(queryKey));

            return matches.length > 0 ? matches : [{ type: 'empty', value: '', label: 'No matching entries.' }];
        },
        onSelect: (option) => {
            select.value = option.value;
            showSelected();
            onChange();
        },
        onClose: showSelected,
        isCurrent: (option) => option.value === select.value,
        filterOnly: true,
    });
    select.hidden = true;
    comboboxElement.hidden = false;

    return {
        combobox,
        selectedName,
        setWording({ label: labelText, none }) {
            label.textContent = labelText;
            select.setAttribute('aria-label', labelText);
            input.placeholder = none;
        },
        reset(targets, excludedId) {
            const choices = targets.filter((target) => target?.id !== excludedId && typeof target?.name === 'string');
            const noMove = document.createElement('option');
            noMove.value = '';
            noMove.textContent = input.placeholder;
            select.replaceChildren(noMove, ...choices.map((target) => {
                const option = document.createElement('option');
                option.value = String(target.id);
                option.textContent = target.name;

                return option;
            }));
            select.value = '';
            showSelected();
            field.hidden = choices.length === 0;
        },
    };
}
